import { useProjectId } from '@config/config.hooks';
import {
  EProjectDiscussionFileAction,
  EProjectDiscussionFileType,
} from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { IUpsertProjectDiscussionFileRequest } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.models';
import { convertDiscussionIdToIdsProps } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.services';
import { FileUploadWithDisplay } from '@file/FileUploadWithDisplay';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { IProjectFileBase } from '@file/projectFile/projectFile.models';
import { useUploadProjectFileDiscussionAttachmentAsync } from '@file/projectFile/projectFile.queries';
import { Avatar, Grid } from '@gcl/react-core';
import { QueryKey } from '@tanstack/react-query';
import * as React from 'react';
import './DiscussionRowLayout.scss';
import { useFetchUserAvatar } from '@user/user.queries';

interface IDiscussionRowLayoutProps {
  contentComponent: React.ReactElement;
  bottomComponent?: React.ReactElement;
  authorName: string;
  authorUid?: string;
  files: IProjectFileBase[];
  isReply?: boolean;
  isDraft?: boolean;
  isSelected?: boolean;
  onClickRow?: () => void;
  discussionId: number;
  fileFormType: EProjectFileType;
  discussionFileType: EProjectDiscussionFileType;
  useFetchFilesQueryKey?: QueryKey;
  disabled?: boolean;
  onFileUploading?: (uuid: string, isUploading: boolean) => void;
}

export const DiscussionRowLayout = ({
  contentComponent,
  bottomComponent,
  authorName,
  authorUid,
  files = [],
  isReply = false,
  isDraft = true,
  isSelected = false,
  onClickRow,
  discussionId,
  fileFormType,
  discussionFileType,
  useFetchFilesQueryKey,
  disabled = false,
  onFileUploading,
}: IDiscussionRowLayoutProps) => {
  const projectId = useProjectId();
  const fileDiscussionIdsProps = React.useMemo(
    () => convertDiscussionIdToIdsProps(discussionId, discussionFileType),
    [discussionId, discussionFileType],
  );
  const { data: authorAvatar } = useFetchUserAvatar(authorUid ?? '');

  return (
    <div
      className={`gcl-gems-discussion-row-layout-container ${isDraft ? 'draft' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={(event) => {
        if (onClickRow !== null && typeof onClickRow === 'function') onClickRow();
      }}
    >
      <Avatar round name={authorName} maxInitials={1} size="40" src={authorAvatar} />
      <Grid container className="gcl-gems-discussion-row-layout-main-content">
        {contentComponent}
        {(files.length > 0 || isDraft) && (
          <Grid item flex="1 1 100%">
            <div
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <FileUploadWithDisplay
                useFetchFilesQueryKey={useFetchFilesQueryKey}
                type={isReply ? 'reply' : 'comment'}
                id={`${discussionId}_${isReply ? 'response' : 'comment'}_attachment`}
                className="gcl-gems-discussions-row-layout-files-display"
                header="Files"
                mode={disabled || !isDraft ? 'View' : 'Update'}
                fileType={fileFormType}
                defaultFiles={files}
                uploadFileRequestParam={
                  isDraft
                    ? {
                        ...fileDiscussionIdsProps,
                        discussionFile: {
                          ...fileDiscussionIdsProps,
                          projectId,
                          type: discussionFileType,
                          action: isReply
                            ? EProjectDiscussionFileAction.RESPONSE
                            : EProjectDiscussionFileAction.COMMENT,
                        } as IUpsertProjectDiscussionFileRequest,
                      }
                    : undefined
                }
                onUploadStatusChanged={onFileUploading}
                generateUseUploadProjectFileAsync={useUploadProjectFileDiscussionAttachmentAsync}
              />
            </div>
          </Grid>
        )}
        {bottomComponent && <div className="gcl-gems-discussions-row-layout-bottom-content">{bottomComponent}</div>}
      </Grid>
    </div>
  );
};
